// =============================================================================
// SIMULACAOCAIXACARD.TSX - OTMC-DEC-0006 / OTMC-CMD-0006 P5
// -----------------------------------------------------------------------------
// O botão da ficha da Operação que abre a simulação (D6.1) e o histórico dos
// cenários já capturados.
//
// COMO O DISPARO FUNCIONA, E POR QUE NÃO É "ABRIR O PAINEL"
//   Uma página web não pode abrir o popup de uma extensão - é fronteira do
//   navegador. Então o CRM ENTREGA o perfil pré-preenchido à extensão, que acende
//   o badge; o operador clica no ícone e encontra a tela de revisão pronta. Um
//   gesto humano permanece no caminho, o que é coerente com o veículo assistido
//   da OTMC-DEC-0005 (nunca há simulação CAIXA sem operador).
//
// ISOLAMENTO (D6.4) - onde ele mora de verdade
//   O perfil sai daqui por GET (somente leitura) e o que o analista editar no
//   painel nunca volta para os campos da Operação: não existe rota que escreva
//   parâmetro de simulação na Operação. Cada execução vira UMA SimulacaoCaixa
//   nova, e N cenários deixam a Operação intacta. Provado em
//   `smoke-simulacao-caixa.ts`, cenário (j).
//
// IDENTIDADE (DEC-0004 D4)
//   Quem faz o POST é esta página, autenticada com o token do operador. A extensão
//   nunca guarda credencial nem fala com o backend em nome de ninguém - o
//   `operadorEmail` é resolvido no servidor a partir do token, ignorando o corpo.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { simulacaoCaixaApi, type SimulacaoCaixaRegistro } from '../services/api';

interface Props {
  operacaoId: string;
  operacaoNumero: string;
}

/** Protocolo da ponte com a extensão (espelha `src/ponte-crm.js`). */
const MARCA = '__otmcCrm';
type MsgDaExtensao = {
  __otmcCrm?: boolean;
  direcao?: string;
  tipo?: string;
  versao?: string;
  ok?: boolean;
  pacote?: Record<string, unknown>;
};

function moeda(v: string | null) {
  if (v === null) return '-';
  return Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function pct(v: string | null) {
  return v === null ? '-' : `${Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 4 })}%`;
}

export default function SimulacaoCaixaCard({ operacaoId, operacaoNumero }: Props) {
  const [extensaoVersao, setExtensaoVersao] = useState<string | null>(null);
  const [gateLigado, setGateLigado] = useState<boolean | null>(null);
  const [simulacoes, setSimulacoes] = useState<SimulacaoCaixaRegistro[]>([]);
  const [faltando, setFaltando] = useState<string[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [preparando, setPreparando] = useState(false);
  const jaAvisouPacote = useRef<string | null>(null);

  const carregarHistorico = useCallback(async () => {
    try {
      setSimulacoes(await simulacaoCaixaApi.listar(operacaoId));
    } catch {
      /* histórico é acessório: sem ele o botão ainda funciona */
    }
  }, [operacaoId]);

  useEffect(() => {
    simulacaoCaixaApi.estado()
      .then((e) => setGateLigado(e.simulacaoHabilitada))
      .catch(() => setGateLigado(null));
    carregarHistorico();
  }, [carregarHistorico]);

  // --- ponte com a extensão -------------------------------------------------
  useEffect(() => {
    async function aoReceber(ev: MessageEvent<MsgDaExtensao>) {
      const m = ev.data;
      if (ev.source !== window || !m || m[MARCA] !== true || m.direcao !== 'para-pagina') return;

      if (m.tipo === 'pong' && m.versao) setExtensaoVersao(m.versao);

      if (m.tipo === 'preparo-registrado') {
        setPreparando(false);
        setAviso(m.ok
          ? 'Perfil enviado à extensão. Abra o ícone da extensão no navegador para revisar e simular.'
          : 'A extensão recusou o preparo. Recarregue a extensão e tente de novo.');
      }

      // Captura concluída no SIOPI: quem grava é esta página, autenticada.
      if (m.tipo === 'simulacao-concluida' && m.pacote) {
        const pacote = m.pacote as Record<string, unknown> & { operacaoId?: string };
        if (pacote.operacaoId !== operacaoId) return;         // captura de outra Operação
        const assinatura = JSON.stringify(pacote).slice(0, 200);
        if (jaAvisouPacote.current === assinatura) return;     // reentrega do mesmo pacote
        jaAvisouPacote.current = assinatura;
        try {
          await simulacaoCaixaApi.registrar(pacote);
          setAviso('Simulação registrada na Operação como cenário SIMULADO.');
          setErro(null);
          await carregarHistorico();
        } catch (e) {
          const status = (e as { response?: { status?: number } }).response?.status;
          setErro(status === 409
            ? 'Captura recebida, mas o módulo está desligado no servidor (SIMULACAO_HABILITADA=false) - nada foi gravado.'
            : 'Não foi possível registrar a simulação recebida da extensão.');
        }
      }
    }

    window.addEventListener('message', aoReceber);
    // Pergunta se a extensão está instalada, para não oferecer um botão que
    // morreria em silêncio.
    window.postMessage({ [MARCA]: true, direcao: 'para-extensao', tipo: 'ping' }, window.location.origin);
    return () => window.removeEventListener('message', aoReceber);
  }, [operacaoId, carregarHistorico]);

  async function handlePrepararSimulacao() {
    setErro(null);
    setAviso(null);
    setPreparando(true);
    try {
      const { perfil, faltando } = await simulacaoCaixaApi.perfil(operacaoId);
      setFaltando(faltando);
      window.postMessage({
        [MARCA]: true,
        direcao: 'para-extensao',
        tipo: 'preparar-simulacao',
        perfil,
        operacaoId,
        operacaoNumero,
      }, window.location.origin);
      // Se a extensão não estiver instalada, ninguém responde `preparo-registrado`.
      setTimeout(() => {
        setPreparando((aindaEsperando) => {
          if (aindaEsperando) {
            setErro('A extensão não respondeu. Confirme que ela está carregada no navegador (ver README da extensão).');
          }
          return false;
        });
      }, 3000);
    } catch {
      setPreparando(false);
      setErro('Não foi possível montar o perfil desta Operação.');
    }
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-800">Simulação CAIXA</h4>
          <p className="mt-0.5 text-xs text-slate-500">
            Roda na extensão, no seu navegador logado no SIOPI. Os valores da Operação
            vão como ponto de partida e <strong>tudo é editável</strong> - editar na
            simulação nunca altera esta Operação.
          </p>
        </div>
        <span
          className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-medium ${
            extensaoVersao ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}
          title={extensaoVersao
            ? 'Extensão detectada neste navegador'
            : 'Extensão não detectada - carregue-a em edge://extensions (Carregar sem compactação)'}
        >
          {extensaoVersao ? `extensão v${extensaoVersao}` : 'extensão não detectada'}
        </span>
      </div>

      <button
        type="button"
        onClick={handlePrepararSimulacao}
        disabled={preparando || !extensaoVersao}
        className="mt-3 w-full rounded-md bg-otm-orange px-3 py-2 text-sm font-medium text-white
          hover:bg-otm-orange-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        {preparando ? 'Enviando à extensão…' : 'Preparar simulação'}
      </button>

      {!extensaoVersao && (
        <p className="mt-2 text-xs text-slate-500">
          Para habilitar: abra <code>edge://extensions</code> (ou <code>chrome://extensions</code>),
          ligue o modo do desenvolvedor e use <strong>Carregar sem compactação</strong> apontando
          para a pasta <code>extensao-caixa/</code>.
        </p>
      )}

      {faltando.length > 0 && (
        <p className="mt-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          Esta Operação não tem {faltando.length} campo(s) que a simulação pede - eles vão
          <strong> em branco</strong>, destacados no painel para você completar:{' '}
          {faltando.join(', ')}.
        </p>
      )}
      {aviso && <p className="mt-2 rounded bg-blue-50 px-2 py-1.5 text-xs text-blue-800">{aviso}</p>}
      {erro && <p className="mt-2 rounded bg-red-50 px-2 py-1.5 text-xs text-red-700">{erro}</p>}
      {gateLigado === false && (
        <p className="mt-2 text-xs text-slate-500">
          Módulo em homologação (<code>SIMULACAO_HABILITADA=false</code>): a simulação roda e
          aparece no painel da extensão, mas ainda não é gravada na Operação.
        </p>
      )}

      {simulacoes.length > 0 && (
        <div className="mt-4">
          <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Cenários simulados ({simulacoes.length})
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-slate-400">
                  <th className="py-1 pr-2 font-medium">Quando</th>
                  <th className="py-1 pr-2 font-medium">Banco / produto</th>
                  <th className="py-1 pr-2 text-right font-medium">Financiamento</th>
                  <th className="py-1 pr-2 text-right font-medium">1ª prestação</th>
                  <th className="py-1 pr-2 text-right font-medium">Cota</th>
                  <th className="py-1 text-right font-medium">Juros efetivos</th>
                </tr>
              </thead>
              <tbody>
                {simulacoes.map((s) => (
                  <tr key={s.id} className="border-t border-slate-100 text-slate-700">
                    <td className="py-1.5 pr-2 whitespace-nowrap">
                      {new Date(s.capturadaEm).toLocaleString('pt-BR', {
                        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="py-1.5 pr-2">
                      <span className="font-medium">{s.banco}</span>
                      {s.produtoCodigo && (
                        <span className="text-slate-400"> · {s.produtoCodigo}/{s.produtoVariante ?? '-'}</span>
                      )}
                    </td>
                    <td className="py-1.5 pr-2 text-right">{moeda(s.valorFinanciamento)}</td>
                    <td className="py-1.5 pr-2 text-right">{moeda(s.primeiraPrestacao)}</td>
                    <td className="py-1.5 pr-2 text-right">{pct(s.cotaMaximaPct)}</td>
                    <td className="py-1.5 text-right">{pct(s.jurosEfetivosPct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-1.5 text-[10px] text-slate-400">
            Todos os registros são <strong>SIMULADO</strong> - nenhuma linha aqui é aprovação
            de crédito nem proposta vinculante.
          </p>
        </div>
      )}
    </div>
  );
}
